import React, { FC } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Category } from 'types/schema';
import { ApplicationRootState } from 'types';
import { selectCategories, selectLang } from 'containers/MainLayout/selectors';
import { generateSlugFromID } from 'utils/urls/categoriesPathSegment';
import { commonDimensions } from 'utils/constants';
import {
  desktop120,
  desktop121,
  desktop122,
  desktop123,
  desktop124,
  desktop125,
  desktop126,
  desktop127,
  desktop128,
  desktop129,
  desktop130,
  desktop131,
  mobile120,
  mobile121,
  mobile122,
  mobile123,
  mobile124,
  mobile125,
  mobile126,
  mobile127,
  mobile128,
  mobile129,
  mobile130,
  mobile131,
} from './images';

/* ─── Image mapping by category ID ───────────────────────────────────────────── */

const DESKTOP_IMAGES: Record<number, string> = {
  120: desktop120,
  121: desktop121,
  122: desktop122,
  123: desktop123,
  124: desktop124,
  125: desktop125,
  126: desktop126,
  127: desktop127,
  128: desktop128,
  129: desktop129,
  130: desktop130,
  131: desktop131,
};

const MOBILE_IMAGES: Record<number, string> = {
  120: mobile120,
  121: mobile121,
  122: mobile122,
  123: mobile123,
  124: mobile124,
  125: mobile125,
  126: mobile126,
  127: mobile127,
  128: mobile128,
  129: mobile129,
  130: mobile130,
  131: mobile131,
};

/* ─── Styled Components ───────────────────────────────────────────────────────── */

const Wrapper = styled.div`
  width: 100%;
`;

const Container = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (max-width: 1023px) {
    padding: 0 16px;
    gap: 8px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

interface CardProps {
  desktopBg: string;
  mobileBg: string;
}

const Card = styled(Link)<CardProps>`
  position: relative;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;
  background-color: #ffffff;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    text-decoration: none;
  }
  // @media (max-width: 1023px) {
  //   height: 95px;
  //   background-image: url(${({ mobileBg }) => mobileBg});
  // }
`;

const CardImg = styled.img`
  position: absolute;
  top: -25px;
  left: -25px;
  width: 120%;
  height: 140%;
  object-fit: cover;
`;

const CardLabel = styled.span`
  position: absolute;
  z-index: 1;
  padding: 8px 12px;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #111827;
  bottom: 10px;
  left: 10px;
  @media (max-width: 1023px) {
    font-size: 12px;
    line-height: 18px;
    padding: 6px 8px;
  }
`;

/* ─── Component ───────────────────────────────────────────────────────────────── */

interface OwnProps {}

interface StateProps {
  categories: Category[];
  lang: string;
}

type Props = OwnProps & StateProps;

const CategoryCardsSection: FC<Props> = ({ categories, lang }) => {
  if (!categories || categories.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <Container>
        <Grid>
          {categories.map((category: Category) => {
            const nameLocal = category.nameLocal || '';
            const desktopBg = DESKTOP_IMAGES[category.id ?? 0] || '';
            const mobileBg = MOBILE_IMAGES[category.id ?? 0] || '';
            const href = `/${generateSlugFromID(category, lang)}`;

            return (
              <Card key={category.id} to={href} desktopBg={desktopBg} mobileBg={mobileBg}>
                <CardImg src={desktopBg} />
                <CardLabel>{nameLocal}</CardLabel>
              </Card>
            );
          })}
        </Grid>
      </Container>
    </Wrapper>
  );
};

/* ─── Redux ───────────────────────────────────────────────────────────────────── */

interface IStateProps {
  categories: Category[];
  lang: string;
}

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  categories: selectCategories(),
  lang: selectLang(),
});

const withConnect = connect(mapStateToProps);

export default compose(withConnect)(CategoryCardsSection);
